import { CLIENT_PAGES } from '@/constants/clientPortalPages'
import type { AddressInfoData } from '@/components/forms/addressInfo'
import { createEmptyAddressInfo } from '@/components/forms/addressInfo'
import type { PersonalInfoData } from '@/components/forms/personalInfo'
import { createEmptyPersonalInfo } from '@/components/forms/personalInfo'
import { defaultPcpInfoValues, type PcpInfoValues } from '@/utilities/pcpForm'
import type { WizardStep } from '@/flows/engine/types'
import {
  addressInfoValidationRules,
  personalInfoValidationRules,
} from '@/utilities/formValidations'
import {
  isEligibleForKitRegistration,
  validateQuestionnaireAnswers,
  type QuestionnaireAnswers,
} from '@/components/questionnaire'
import { resolveEligibilityQuestionnaire } from '@/flows/utils/partnerQuestionnaires'
import {
  insertStepAfterAnchor,
  readEligibilityQuestionnairePositionFromQuestionnaire,
  type EligibilityQuestionnairePosition,
} from '@/flows/utils/eligibilityQuestionnairePosition'
import type { QuestionnaireDTOType } from '@/types/portal-api.generated'

/**
 * Canonical ordered catalog. `eligibility-questionnaire` sits after kit ID as its
 * catalog slot; runtime lists omit it unless the EQ flag is on, then insert it after
 * the questionnaire anchor. `pcp-info` sits after address; omit it at runtime when the
 * PCP flag is off. `success` sits last and is omitted from progress (reviewable) lists.
 */
export const CLIENT_KIT_REGISTRATION_CATALOG = [
  { key: 'kit-validation', name: 'Kit ID' },
  { key: 'eligibility-questionnaire', name: 'Eligibility Questionnaire' },
  { key: 'consent', name: 'Consent Agreements' },
  { key: 'patient-info', name: 'Patient Information' },
  { key: 'address-info', name: 'Address Information' },
  { key: 'pcp-info', name: 'Primary Care Provider' },
  { key: 'review', name: 'Review & Submit' },
  { key: 'success', name: 'Success' },
] as const

export type ClientKitRegistrationStepKey = (typeof CLIENT_KIT_REGISTRATION_CATALOG)[number]['key']

export const CLIENT_KIT_REGISTRATION_STEP_KEYS: readonly ClientKitRegistrationStepKey[] =
  CLIENT_KIT_REGISTRATION_CATALOG.map((step) => step.key)

export type ClientKitRegistrationStep = WizardStep & {
  key: ClientKitRegistrationStepKey
}

export const CLIENT_KIT_REGISTRATION_ELIGIBILITY_QUESTIONNAIRE_STEP =
  CLIENT_KIT_REGISTRATION_CATALOG.find((step) => step.key === 'eligibility-questionnaire')!

export const CLIENT_KIT_REGISTRATION_PCP_STEP = CLIENT_KIT_REGISTRATION_CATALOG.find(
  (step) => step.key === 'pcp-info',
)!

export const CLIENT_KIT_REGISTRATION_SUCCESS_STEP = CLIENT_KIT_REGISTRATION_CATALOG.find(
  (step) => step.key === 'success',
)!

export const CLIENT_KIT_REGISTRATION_FLOW = {
  id: 'client-kit-registration',
  name: 'Kit Registration',
  baseUrl: CLIENT_PAGES.kitRegistration,
  steps: CLIENT_KIT_REGISTRATION_CATALOG,
} as const

export const CLIENT_KIT_REGISTRATION_FIRST_STEP = CLIENT_KIT_REGISTRATION_CATALOG[0]

export const CLIENT_KIT_REGISTRATION_FIRST_STEP_KEY = CLIENT_KIT_REGISTRATION_FIRST_STEP.key

export const CLIENT_KIT_REGISTRATION_FIRST_STEP_PATH = CLIENT_KIT_REGISTRATION_FLOW.baseUrl

const CLIENT_ELIGIBILITY_QUESTIONNAIRE_POSITION_TO_ANCHOR: Record<
  EligibilityQuestionnairePosition,
  ClientKitRegistrationStepKey
> = {
  after_kit_id: 'kit-validation',
  after_consent: 'consent',
  after_personal_info: 'patient-info',
  after_address_info: 'address-info',
}

export type ClientKitRegistrationStepListOptions = {
  includePcpStep?: boolean
  includeEligibilityQuestionnaireStep?: boolean
  eligibilityQuestionnairePosition?: EligibilityQuestionnairePosition
}

function insertClientEligibilityQuestionnaireStep(
  steps: ClientKitRegistrationStep[],
  position: EligibilityQuestionnairePosition,
): ClientKitRegistrationStep[] {
  return insertStepAfterAnchor(steps, {
    position,
    positionToAnchor: CLIENT_ELIGIBILITY_QUESTIONNAIRE_POSITION_TO_ANCHOR,
    stepToInsert: {
      key: CLIENT_KIT_REGISTRATION_ELIGIBILITY_QUESTIONNAIRE_STEP.key,
      name: CLIENT_KIT_REGISTRATION_ELIGIBILITY_QUESTIONNAIRE_STEP.name,
    },
  })
}

function selectClientKitRegistrationSteps(
  options: ClientKitRegistrationStepListOptions & { includeSuccess?: boolean } = {},
): ClientKitRegistrationStep[] {
  const catalogSteps = CLIENT_KIT_REGISTRATION_CATALOG.filter((step) => {
    if (step.key === 'eligibility-questionnaire') return false
    if (step.key === 'pcp-info' && !options.includePcpStep) return false
    if (step.key === 'success' && !options.includeSuccess) return false
    return true
  }).map((step) => ({ key: step.key, name: step.name }))

  if (!options.includeEligibilityQuestionnaireStep) {
    return catalogSteps
  }

  return insertClientEligibilityQuestionnaireStep(
    catalogSteps,
    options.eligibilityQuestionnairePosition ?? 'after_kit_id',
  )
}

/**
 * Full step list (including `success`) as a mutable `WizardStep[]`, for mounting
 * `useWizard` (kernel `steps` option requires a mutable array; the catalog is a
 * frozen `as const` literal). `success` is included so the kernel's own reconciliation
 * never treats it as an invalid/missing key once the flow completes.
 */
export function getClientKitRegistrationWizardSteps(
  options: ClientKitRegistrationStepListOptions = {},
): ClientKitRegistrationStep[] {
  return selectClientKitRegistrationSteps({ ...options, includeSuccess: true })
}

/**
 * The reviewable steps only (excludes `success`), as a mutable `WizardStep[]`, for the
 * progress adapter — the Client progress indicator has never counted `success` as a step
 * (see the pre-kernel `KIT_REGISTRATION_PROGRESS_STEP_COUNT`/`KIT_REGISTRATION_STEP_TITLES`
 * constants this replaces). When the PCP flag is off, `pcp-info` is omitted from its slot.
 * The eligibility questionnaire is omitted unless its flag is on.
 */
export function getClientKitRegistrationReviewableSteps(
  options: ClientKitRegistrationStepListOptions = {},
): ClientKitRegistrationStep[] {
  return selectClientKitRegistrationSteps(options)
}

export type ClientKitValidationInfo = {
  kitId: string
  validationStatus: 'valid'
  validatedAt: string
  sku?: string
  kitStateIsNy?: boolean
  skipRegistration?: boolean
}

export type ClientKitRegistrationPatientInfo = PersonalInfoData
export type ClientKitRegistrationAddressInfo = AddressInfoData
export type ClientKitRegistrationPcpInfo = PcpInfoValues

export type ClientKitRegistrationConsentInfo = {
  atHomeConsentAgreed: boolean
  hipaaConsentAgreed: boolean
  consentedAt?: string
}

export type ClientKitRegistrationEligibilityQuestionnaireInfo = {
  answers: QuestionnaireAnswers
  activePageIndex: number
}

export type ClientKitRegistrationFormData = {
  kitInfo?: ClientKitValidationInfo
  patientInfo: ClientKitRegistrationPatientInfo
  addressInfo: ClientKitRegistrationAddressInfo
  consentInfo: ClientKitRegistrationConsentInfo
  pcpInfo: ClientKitRegistrationPcpInfo
  eligibilityQuestionnaire?: ClientKitRegistrationEligibilityQuestionnaireInfo
}

export function isClientKitRegistrationStepKey(
  stepKey: string,
): stepKey is ClientKitRegistrationStepKey {
  return CLIENT_KIT_REGISTRATION_STEP_KEYS.includes(stepKey as ClientKitRegistrationStepKey)
}

export function getClientKitRegistrationStep(stepKey: ClientKitRegistrationStepKey) {
  return CLIENT_KIT_REGISTRATION_CATALOG.find((step) => step.key === stepKey)
}

export function getClientKitRegistrationStepIndex(
  stepKey: ClientKitRegistrationStepKey,
  steps: readonly ClientKitRegistrationStep[] = getClientKitRegistrationReviewableSteps(),
) {
  if (stepKey === 'success') {
    return steps.length
  }
  return steps.findIndex((step) => step.key === stepKey)
}

export function createEmptyClientKitRegistrationFormData(): ClientKitRegistrationFormData {
  return {
    patientInfo: createEmptyPersonalInfo(),
    addressInfo: createEmptyAddressInfo(),
    consentInfo: {
      atHomeConsentAgreed: false,
      hipaaConsentAgreed: false,
    },
    pcpInfo: { ...defaultPcpInfoValues },
  }
}

function withoutEligibilityQuestionnaire(
  formData: ClientKitRegistrationFormData,
): ClientKitRegistrationFormData {
  if (!formData.eligibilityQuestionnaire) return formData
  const { eligibilityQuestionnaire: _eligibilityQuestionnaire, ...rest } = formData
  return rest
}

function didValidatedKitIdentityChange(
  current: ClientKitValidationInfo | undefined,
  next: ClientKitValidationInfo,
): boolean {
  return current?.kitId !== next.kitId || current?.sku !== next.sku
}

/** Drop PHI questionnaire answers whenever the validated kit/SKU is replaced. */
export function applyValidatedClientKit(
  formData: ClientKitRegistrationFormData,
  kitInfo: ClientKitValidationInfo,
): ClientKitRegistrationFormData {
  const nextFormData = didValidatedKitIdentityChange(formData.kitInfo, kitInfo)
    ? withoutEligibilityQuestionnaire(formData)
    : formData
  return { ...nextFormData, kitInfo }
}

/** Drop kit validation and questionnaire answers together so stale PHI cannot follow a new kit. */
export function withoutValidatedClientKit(
  formData: ClientKitRegistrationFormData,
): ClientKitRegistrationFormData {
  const { kitInfo: _kitInfo, ...rest } = withoutEligibilityQuestionnaire(formData)
  return rest
}

export function isClientKitRegistrationConsentComplete(
  consentInfo: ClientKitRegistrationConsentInfo,
): boolean {
  return consentInfo.atHomeConsentAgreed && consentInfo.hipaaConsentAgreed
}

export function shouldIncludeClientEligibilityQuestionnaireStep(options: {
  isEligibilityQuestionnaireFlagEnabled: boolean
  skipRegistration?: boolean
  kitValidated?: boolean
  partnerId: string
  sku?: string | null
}): boolean {
  if (!options.isEligibilityQuestionnaireFlagEnabled) return false
  if (options.kitValidated !== true) return false
  if (options.skipRegistration === true) return false
  return resolveEligibilityQuestionnaire(options.partnerId, options.sku) !== null
}

export function resolveClientEligibilityQuestionnaire(
  partnerId: string,
  sku?: string | null,
): QuestionnaireDTOType | null {
  return resolveEligibilityQuestionnaire(partnerId, sku)
}

export function getClientEligibilityQuestionnairePosition(
  questionnaire: QuestionnaireDTOType | null,
): EligibilityQuestionnairePosition {
  return readEligibilityQuestionnairePositionFromQuestionnaire(questionnaire)
}

export function resolveClientKitRegistrationStartStep(
  requestedStepKey: ClientKitRegistrationStepKey,
  steps: readonly ClientKitRegistrationStep[],
): ClientKitRegistrationStepKey {
  const eligibilityIndex = steps.findIndex((step) => step.key === 'eligibility-questionnaire')
  const requestedIndex = steps.findIndex((step) => step.key === requestedStepKey)
  if (eligibilityIndex !== -1 && (requestedIndex === -1 || eligibilityIndex < requestedIndex)) {
    return 'eligibility-questionnaire'
  }
  return requestedStepKey
}

export function isClientEligibilityQuestionnaireComplete(
  questionnaire: QuestionnaireDTOType | null | undefined,
  answers: QuestionnaireAnswers | undefined,
): boolean {
  if (!questionnaire) return false
  return validateQuestionnaireAnswers(questionnaire, answers ?? {}).isValid
}

/** True when answers are complete and kit registration is allowed. */
export function isClientEligibilityQuestionnaireReadyForRegistration(
  questionnaire: QuestionnaireDTOType | null | undefined,
  answers: QuestionnaireAnswers | undefined,
): boolean {
  if (!questionnaire || !isClientEligibilityQuestionnaireComplete(questionnaire, answers)) {
    return false
  }
  return isEligibleForKitRegistration(questionnaire, answers ?? {})
}

export const clientKitRegistrationPatientInfoValidationRules =
  personalInfoValidationRules<ClientKitRegistrationPatientInfo>()

export const clientKitRegistrationAddressInfoValidationRules =
  addressInfoValidationRules<ClientKitRegistrationAddressInfo>()
